import { Card, Typography, Empty, Button } from 'antd';
import { PrinterOutlined, SaveOutlined } from '@ant-design/icons';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import PatientCCPaperView from '../../components/PatientCCPaperView/PatientCCPaperView';

// Hooks and Components
import { usePatientPlanDetailsLogic } from './hooks/usePatientPlanDetailsLogic';
import TreatmentCard from './components/TreatmentCard';
import { Tag } from 'feather-icons-react/build/IconComponents';

const { Title, Text } = Typography;

/**
 * PatientPlanDetails - Main Component
 * Displays full treatment details for a specific patient plan (service bookings)
 */
const PatientPlanDetails = () => {
    const {
        orderId,
        guestId,
        planData,
        loader,
        patientInfo,
        handlePrint,
        formatTime,
        handlePlanChange,
        handleSave,
        isSaving,
        hasChanges
    } = usePatientPlanDetailsLogic();

    return (
        <>
            <Header />
            <Sidebar />
            <div className="page-wrapper" style={{ paddingBottom: '100px' }}>
                <div className="content">
                    {/* Simplified Header */}
                    <div style={{ marginBottom: '24px' }}>
                        <Title level={2} style={{ margin: 0, fontWeight: 700 }}>Treatment Details</Title>
                        <Text type="secondary">Review and manage treatment schedules</Text>
                    </div>

                    {/* Patient Information Summary */}
                    <div style={{ marginBottom: '40px' }}>
                        <PatientCCPaperView
                            userId={guestId}
                            extraInfo={{
                                "Registration No": patientInfo.registrationNo || 'N/A',
                                "Order ID": orderId !== 'none' ? orderId : 'N/A'
                            }}
                        />
                    </div>

                    {/* Treatment Plans List */}
                    {loader ? (
                        <div style={{ padding: '60px 0' }}>
                            <Card loading={true} bordered={false} style={{ borderRadius: '16px' }} />
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                            {planData?.map((plan) => (
                                <TreatmentCard
                                    key={plan._id}
                                    plan={plan}
                                    formatTime={formatTime}
                                    handlePlanChange={handlePlanChange}
                                />
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {!loader && (!planData || planData.length === 0) && (
                        <Card bordered={false} style={{ textAlign: 'center', padding: '80px 0', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <div style={{ marginTop: '16px' }}>
                                        <Text strong style={{ fontSize: '18px', display: 'block' }}>No Treatment Plans Found</Text>
                                        <Text type="secondary">{"We couldn't find any service details for this record."}</Text>
                                    </div>
                                }
                            />
                        </Card>
                    )}
                </div>
            </div>

            {/* Fixed Bottom Action Bar */}
            <div style={{
                position: 'fixed',
                bottom: 0,
                right: 0,
                left: '250px', // Sidebar width accommodation
                background: '#fff',
                padding: '16px 40px',
                boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                zIndex: 1000,
                borderTop: '1px solid #f0f0f0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {hasChanges && (
                        <Tag color="orange" style={{ margin: 0, padding: '4px 12px', borderRadius: '4px' }}>
                            Pending Changes
                        </Tag>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                    <Button
                        icon={<PrinterOutlined />}
                        onClick={handlePrint}
                        size="large"
                        style={{ borderRadius: '8px' }}
                    >
                        Print Full Plan
                    </Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSave}
                        loading={isSaving}
                        disabled={!hasChanges}
                        size="large"
                        style={{
                            background: hasChanges ? '#52c41a' : '#f5f5f5',
                            borderColor: hasChanges ? '#52c41a' : '#d9d9d9',
                            minWidth: '160px',
                            borderRadius: '8px',
                            fontWeight: 600
                        }}
                    >
                        {hasChanges ? 'Save Changes' : 'All Changes Saved'}
                    </Button>
                </div>
            </div>

            {/* Global Table Styles */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-table .ant-table-thead > tr > th {
                    background: #f8faff;
                    font-weight: 600;
                    color: #4b5563;
                }
                .custom-table .ant-table-tbody > tr:hover > td {
                    background: #f3f4f6 !important;
                }
                .custom-table {
                    border-radius: 0 0 12px 12px;
                    overflow: hidden;
                }
                .page-wrapper {
                    transition: all 0.2s ease;
                }
                @media (max-width: 991px) {
                    .page-wrapper { padding-bottom: 120px !important; }
                    div[style*="position: fixed"] { left: 0 !important; padding: 16px 20px !important; }
                }
            `}} />
        </>
    );
};

export default PatientPlanDetails;
